'use client';

import { useLanguage } from '@bells/core/i18n';
import { cn } from '@bells/core/lib/utils';
import type { Locale } from '@bells/i18n/runtime';
import { Button } from '@bells/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '@bells/ui/components/command';
import { first } from 'lodash';
import { Hourglass } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  formatDuration,
  formatDurationPlane,
  moneyFormatter,
} from '@/components/girl/utils';
import {
  attendantSearchChooseDuration,
  locationSearchChange,
  locationSearchDurationDesktop,
} from '@bells/i18n';
import {
  AttendantSearchSingleResponse,
  PriceArrayKeys,
} from '@bells/schema/models/attendant';
import { CommandItem } from '@bells/ui/components/command';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@bells/ui/components/drawer';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@bells/ui/components/popover';
import { useMediaQuery } from '@bells/ui/hooks/use-media-query';
import { Check } from 'lucide-react';
import { useRequest } from '@/components/girl/request-context';

const DurationList = ({
  disabled,
  notFoundLabel = 'Keine Duration gefunden',
  currentDuration,
  setCurrentDuration,
  list,
  locale,
}: {
  disabled?: boolean;
  notFoundLabel?: string;
  currentDuration?: number;
  setCurrentDuration?: (val: number) => void;
  locale: Locale;
  list: {
    price: number;
    hour: number;
  }[];
}) => {
  const [search, setSearch] = useState('');

  return (
    <Command className='bg-transparent'>
      <CommandInput value={search} onValueChange={setSearch} />
      <CommandList>
        <CommandEmpty className='p-4'>{notFoundLabel}</CommandEmpty>
        <CommandGroup>
          {list.map((item) => {
            return (
              <CommandItem
                key={`duration-${item.hour}`}
                disabled={disabled}
                value={formatDurationPlane(item.hour, locale)}
                onSelect={() => {
                  setCurrentDuration?.(item.hour);
                }}
              >
                <div className='flex w-full items-center justify-between gap-2'>
                  {formatDurationPlane(item.hour, locale)}
                  <div>{moneyFormatter.format(item.price)}</div>
                </div>

                <Check
                  className={cn(
                    'ml-auto',
                    item.hour === currentDuration ? 'opacity-100' : 'opacity-0'
                  )}
                ></Check>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

type LocationSearchInternalProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'onSelect' | 'onDurationChange'
> & {
  duration?: number;
  list: {
    price: number;
    hour: number;
  }[];
  onSubmit?: () => void;
  language: Locale;
  onDurationChange: (duration: number) => void;
};

const LocationSearchDesktop = React.forwardRef<
  HTMLDivElement,
  LocationSearchInternalProps
>(({ duration, language, onDurationChange, list }, ref) => {
  const [openDuration, setOpenDuration] = useState(false);

  const activeValue = useMemo(() => {
    const activeDuration = list.find((item) => item.hour === duration);
    return activeDuration;
  }, [list, duration]);

  return (
    <div ref={ref} className='w-full'>
      <Popover modal open={openDuration} onOpenChange={setOpenDuration}>
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant={'ghost'}
            className={cn(
              'w-full break-all border border-input px-2 py-4 pl-3 text-left text-foreground ring-0 hover:ring-0 focus:ring-0 focus-visible:ring-0',
              !duration && 'text-muted-foreground'
            )}
          >
            {activeValue ? (
              <>
                <span>{formatDurationPlane(activeValue.hour, language)}</span>
                {` `}
                <span className='text-sm text-muted-foreground'>
                  ({moneyFormatter.format(activeValue.price)})
                </span>
              </>
            ) : (
              <span>{attendantSearchChooseDuration()}</span>
            )}
            <Hourglass className='ml-auto h-4 w-4' />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='max-h-screen max-w-[30rem] overflow-auto p-0'
          align='start'
        >
          <DurationList
            locale={language}
            currentDuration={duration}
            list={list}
            setCurrentDuration={(val) => {
              setOpenDuration(false);
              onDurationChange(val);
            }}
          ></DurationList>
        </PopoverContent>
      </Popover>
    </div>
  );
});

LocationSearchDesktop.displayName = 'LocationSearchDesktop';

const LocationSearchMobile = React.forwardRef<
  HTMLDivElement,
  LocationSearchInternalProps
>(({ duration, language, onDurationChange, list }, ref) => {
  const [openDuration, setOpenDuration] = useState(false);
  const activeValue = useMemo(() => {
    const activeDuration = list.find((item) => item.hour === duration);
    return activeDuration;
  }, [list, duration]);
  return (
    <div className='flex w-full flex-col gap-1'>
      <Drawer modal open={openDuration} onOpenChange={setOpenDuration}>
        <DrawerTrigger asChild>
          <Button
            type='button'
            variant={'outline'}
            className={cn(
              'flex h-auto w-full max-w-full items-center justify-between gap-2 overflow-hidden px-2 text-left'
            )}
          >
            <span
              className={cn('line-clamp-1 flex-1 whitespace-normal', {
                'text-muted-foreground': !duration,
              })}
            >
              {activeValue ? (
                <>
                  <span>{formatDurationPlane(activeValue.hour, language)}</span>
                  {` `}
                  <span className='text-sm text-muted-foreground'>
                    ({moneyFormatter.format(activeValue.price)})
                  </span>
                </>
              ) : (
                <span>{attendantSearchChooseDuration()}</span>
              )}
            </span>
            <Hourglass className='ml-auto h-4 w-4' />
          </Button>
        </DrawerTrigger>
        <DrawerContent>
          <DrawerHeader className='text-left'>
            <DrawerTitle>{locationSearchDurationDesktop()}</DrawerTitle>
          </DrawerHeader>
          <div className='relative w-full flex-1'>
            <DurationList
              locale={language}
              currentDuration={duration}
              list={list}
              setCurrentDuration={(val) => {
                onDurationChange(val);
                setOpenDuration(false);
              }}
            ></DurationList>
          </div>
          <DrawerFooter className='pt-2'>
            <DrawerClose asChild>
              <Button variant={'default'} type='button'>
                {locationSearchChange()}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
});

LocationSearchMobile.displayName = 'LocationSearchMobile';

const DurationPicker = React.forwardRef<
  HTMLDivElement,
  Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
    onChange?: (val?: number) => void;
    duration?: number;
  }
>(({ onChange, duration }, ref) => {
  const { language } = useLanguage();

  const { dataDuration } = useRequest();

  const isDesktop = useMediaQuery('(min-width: 768px)');

  const onSubmit = () => {};

  return isDesktop ? (
    <LocationSearchDesktop
      onSubmit={onSubmit}
      ref={ref}
      onDurationChange={(val) => onChange?.(val)}
      language={language}
      duration={duration}
      list={dataDuration}
    ></LocationSearchDesktop>
  ) : (
    <LocationSearchMobile
      onSubmit={onSubmit}
      ref={ref}
      onDurationChange={(val) => onChange?.(val)}
      language={language}
      duration={duration}
      list={dataDuration}
    ></LocationSearchMobile>
  );
});
DurationPicker.displayName = 'DurationPicker';
export { DurationPicker };
